import { defineStore } from "pinia";
import { getVeterinaire } from "@/api/auth";
import { getCustomer, updateCustomer, changeCustomerPassword } from "@/api/services/esupplyvet/customersService";
import { updateAddress } from "@/api/services/esupplyvet/addressesService";
import { removeFromCache } from "@/utils/cacheUtils";

export const useRegisterStore = defineStore("register", {
  state: () => ({
    user: {
      email: "",
      code_clinique: "",
      id: null,
      isProfileIncomplete: false,
      isExistingCustomer: false,
    },
    VeterinaireInfo: {},
    VeterinaireData: {},
    FilterProducts: {},
    clientInfo: {},
    isEditingClient: false,
    loading: false,
    error: null,
    lastVeterinaryFetchTime: 0,
    veterinaryCacheDuration: 5 * 60 * 1000,
    apiResponse: null,
    registrationInProgress: false, // ← Nouvel état pour suivre le processus
  }),
  getters: {
    Veterinaire: state => state.VeterinaireData ?? [],
    isProfileIncomplete: (state) => state.user.isProfileIncomplete,
    isExistingCustomer: (state) => state.user.isExistingCustomer,
    hasRegistrationData: (state) => { // ← Nouveau getter
      return !!(state.user.email || state.user.code_clinique || state.apiResponse);
    },
  },
  actions: {
    // ← ACTIONS EXISTANTES
    setStepOne(data) {
      this.user.email = data.email;
      this.user.code_clinique = data.code_clinique;
    },
    setUser(data) {
      this.user = { ...this.user, ...data };
    },
    setInfoveterinaire(data) {
      this.VeterinaireInfo = data;
    },
    setApiResponse(response) {
      this.apiResponse = response;
    },
    setProfileIncomplete(status) {
      this.user.isProfileIncomplete = status;
    },
    setExistingCustomer(status) {
      this.user.isExistingCustomer = status;
    },
    
  
    startRegistration() {
      this.registrationInProgress = true;
    },
    
    completeRegistration() {
      this.resetProfileFlags();
      this.registrationInProgress = false;
      this.user.isProfileIncomplete = false;
      this.user.isExistingCustomer = false;
      this.apiResponse = null;
    },
    
    cancelRegistration() {
      this.clearRegistrationData();
      this.registrationInProgress = false;
    },
    
    resetProfileFlags() {
      this.user.isProfileIncomplete = false;
      this.user.isExistingCustomer = false;
      this.apiResponse = null;
    },
    
    clearRegistrationData() {
      this.user = {
        email: "",
        code_clinique: "",
        id: null,
        isProfileIncomplete: false,
        isExistingCustomer: false,
      };
      this.VeterinaireInfo = {};
      this.apiResponse = null;
      this.registrationInProgress = false;
      this.error = null;
    },
    
    clearSensitiveData() {
      this.user.password = "";
      this.user.confirm_password = "";
      this.user.confirm_email = "";
    },

    async updateVeterinaire(updatedData) {
      this.VeterinaireData = updatedData;
    },
    SaveFilterProducts(data) {
      this.FilterProducts = data;
    },

    DeleteFilterProducts() {
      this.FilterProducts = {};
    },
    async fetchVeterinaire(forceRefresh = false) {
      const now = Date.now();
      if (
        !forceRefresh &&
        this.VeterinaireData &&
        Object.keys(this.VeterinaireData).length > 0 &&
        now - this.lastVeterinaryFetchTime < this.veterinaryCacheDuration
      ) {
        console.log("Utilisation du cache pour les données vétérinaire");
        return this.VeterinaireData;
      }

      this.loading = true;
      this.error = null;
      try {
        const id = this.VeterinaireInfo.customer_infos?.id || this.user.id;
        if (!id) {
          throw new Error("Customer ID not found");
        }
        const response = await getVeterinaire(id,forceRefresh);
        this.VeterinaireData = response.data;
        this.lastVeterinaryFetchTime = now;
        return this.VeterinaireData;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    hasEssentialVeterinaryData() {
      return (
        this.VeterinaireData &&
        Object.keys(this.VeterinaireData).length > 0 &&
        (this.VeterinaireData.hasSubscription ||
          this.VeterinaireData.adressesFacturation ||
          this.VeterinaireData.lastName)
      );
    },
    refreshVeterinaryCache() {
      this.lastVeterinaryFetchTime = 0;
      return this.fetchVeterinaire(true);
    },
    async fetchClientInfo() {
      this.loading = true;
      this.error = null;
      try {
        const id = this.user.id || this.VeterinaireInfo?.customer_infos?.id;
        const response = await getCustomer(id);
        this.clientInfo = response.data;
      } catch (error) {
        this.error = "Erreur lors du chargement des informations client";
      } finally {
        this.loading = false;
      }
    },
    async updateClientInfo(data) {
      this.loading = true;
      this.error = null;
      try {
        const id = this.user.id || this.VeterinaireInfo?.customer_infos?.id;
        removeFromCache(`customer_${id}`);
        removeFromCache(`veterinaire_${id}`);
        const response = await updateCustomer(id, data);
        this.clientInfo = response.data;
        this.isEditingClient = false;
      } catch (error) {
        this.error = "Erreur lors de la sauvegarde des informations client";
      } finally {
        this.loading = false;
      }
    },
    async updateClientAddress(addressData) {
      this.loading = true;
      this.error = null;
      try {
        const id = this.user.id || this.VeterinaireInfo?.customer_infos?.id;
        const addressId = this.clientInfo?.adressesFacturation?.id;
        if (!addressId) {
          throw new Error("ID d'adresse non trouvé");
        }
        removeFromCache(`customer_${id}`);
        removeFromCache(`veterinaire_${id}`);
        await updateAddress(addressId, addressData);
        await this.fetchClientInfo();
        this.isEditingClient = false;
      } catch (error) {
        this.error = "Erreur lors de la sauvegarde de l'adresse";
      } finally {
        this.loading = false;
      }
    },
    async updateClientAndAddress(clientData, addressData) {
      this.loading = true;
      this.error = null;
      try {
        const id = this.user.id || this.VeterinaireInfo?.customer_infos?.id;
        const addressId = this.clientInfo?.adressesFacturation?.id;
        removeFromCache(`customer_${id}`);
        removeFromCache(`veterinaire_${id}`);
        if (clientData) {
          await updateCustomer(id, clientData);
        }
        if (addressData && addressId) {
          await updateAddress(addressId, addressData);
        }
        await this.fetchClientInfo();
        this.isEditingClient = false;
      } catch (error) {
        if (error.message?.includes("customer")) {
          this.error = "Erreur lors de la sauvegarde des informations client";
        } else if (error.message?.includes("address")) {
          this.error = "Erreur lors de la sauvegarde de l'adresse";
        } else {
          this.error = "Erreur lors de la sauvegarde";
        }
      } finally {
        this.loading = false;
      }
    },
    async changeClientPassword(passwordData) {
      this.loading = true;
      this.error = null;
      try {
        const id = this.user.id || this.VeterinaireInfo?.customer_infos?.id;
        await changeCustomerPassword(id, passwordData);
        return true;
      } catch (error) {
        this.error = "Erreur lors du changement de mot de passe";
        return false;
      } finally {
        this.loading = false;
      }
    },
    toggleClientEdit() {
      this.error = null;
      this.isEditingClient = !this.isEditingClient;
    },
  },
  persist: true,
});
